import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  EMPTY_PLAYBACK_STATE,
  PendingPlaybackRef,
  PlaybackAvailableType,
  PlaybackController,
  PlaybackMode,
  PlaybackState,
} from '../shared/contracts/playback-controller';
import { PlaybackStep } from '../shared/contracts/vessel.types';

/**
 * Voyage Radar's snapshot-replay state — the generic contract plus the four
 * fields only this controller writes and only VR surfaces read. Extracted off
 * `PlaybackState` (phase 0, REFACTOR-PLAN.md §5.4) so a new operator's state is
 * not a union of every other operator's fields. `playbackLoading` itself stayed
 * generic: the shared playback-history entry control gates on it.
 */
export interface VoyageRadarPlaybackState extends PlaybackState {
  /**
   * `playbackLoading`, delayed — what the UI should DRAW.
   *
   * Same reasoning as `trackLoadingVisible`: a cached window re-enters in a few
   * milliseconds, and drawing the honest flag would blink a spinner on exactly
   * those. See `setPlaybackLoading`.
   */
  playbackLoadingVisible?: boolean;
  /** What the in-flight replay is FOR, so the wait can name it. Null when idle. */
  pendingPlayback?: PendingPlaybackRef | null;
  /**
   * refreshId of the frame currently on screen, or null outside playback.
   *
   * Published so the Refresh History list can mark the snapshot being played the
   * way the Vessel Tracks list marks the track being drawn — without it the panel
   * sits still through the one thing it exists to narrate.
   */
  playbackRefreshId?: string | null;
  /**
   * Snapshots that were in the window but did not load (0 when all arrived).
   *
   * Surfaced rather than swallowed: a replay quietly missing a fifth of its
   * frames looks exactly like a replay of an archive that was always that size.
   */
  playbackSkipped?: number;
}

/** The generic defaults plus this controller's own — one per extracted field. */
const EMPTY_VR_PLAYBACK_STATE: VoyageRadarPlaybackState = {
  ...EMPTY_PLAYBACK_STATE,
  playbackLoadingVisible: false,
  pendingPlayback: null,
  playbackRefreshId: null,
  playbackSkipped: 0,
};

/** One playback frame = a saved refresh-history AOI snapshot at a point in time. */
// `VoyagePlaybackStep` was an alias for the promoted `PlaybackStep`
// (phase 8a). Phase 8b flipped the imports and deleted it.


/**
 * Voyage Radar playback/live controller — the pull-based counterpart to
 * `AisstreamPlaybackController`.
 *
 * Unlike AISstream (server-sent live stream), Voyage Radar has NO live socket:
 * the toolbar shows a manual `refresh` + `last-updated` timestamp instead of a
 * pulsing LIVE badge, and "playback" replays the already-saved refresh-history
 * S3 snapshots (0 operator credits). Vessel rendering stays in the map component
 * (`convertToGeoJSONNew` + Leaflet), which this controller drives through
 * host-provided callbacks so the toolbar never needs to know about Leaflet or
 * GeoJSON. See plans/VOYAGE_RADAR_IMPLEMENTATION_PLAN.md (Phase 8C).
 */
@Injectable({ providedIn: 'root' })
export class VoyageRadarPlaybackController extends PlaybackController {
  private _state$ = new BehaviorSubject<VoyageRadarPlaybackState>({ ...EMPTY_VR_PLAYBACK_STATE });
  readonly state$: Observable<VoyageRadarPlaybackState> = this._state$.asObservable();

  private _lastUpdated: string | null = null;
  private _loading = false;
  private _typeFilter = new Set<string>();
  private _availableTypes: PlaybackAvailableType[] = [];

  // ── Playback (refresh-history snapshots) ─────────────────────────────────────
  private static readonly TICK_MS = 100;
  /**
   * Real ms a whole archive takes to play at 1x, where the frame count allows it.
   *
   * The same budget `VoyageTrackController.BASE_PLAY_MS` gives a track, for the
   * same reason: what is being replayed spans anything from twenty minutes to
   * three months, and a rate fixed in SIMULATED time is unusable at one end or
   * the other. This is what the flip-book used to get wrong — speed was
   * simulated-seconds per real-second (default 60), so two refreshes an hour
   * apart held one static frame for a real minute, and a week of history took
   * 2.8 hours to play at the default and ten minutes at the fastest speed
   * offered. The transport was not slow, it was dead.
   */
  private static readonly BASE_PLAY_MS = 24000;
  /**
   * Floor and ceiling on how long ONE frame is held.
   *
   * The difference from the track, and the reason this is a dwell rather than a
   * scaled clock: a track is a continuous path sampled often, so stretching its
   * real timing over the budget keeps the ghost moving the whole way. An archive
   * is a handful of discrete captures at whatever moments an operator happened to
   * press refresh — spreading THAT over the budget in proportion to real time
   * would park the map on one frame for twenty seconds because nobody refreshed
   * over a weekend. Every snapshot gets the same turn instead, and the clamps
   * keep the result readable at both extremes: a 2-frame archive is a 1.5s flip
   * rather than a 24-second stare, and a 300-frame one runs at 5 fps rather than
   * a strobe.
   */
  private static readonly MIN_FRAME_MS = 200;
  private static readonly MAX_FRAME_MS = 1500;
  /**
   * How long a load may run before it is worth drawing.
   *
   * Same 150ms as `VoyageTrackController.LOADING_DELAY_MS`, and the same
   * argument: re-entering a window that is still in the step cache resolves in a
   * few milliseconds, and a spinner that lives for one frame reads as a glitch
   * rather than as speed.
   */
  private static readonly LOADING_DELAY_MS = 150;
  /** Once shown, the smallest time the pending row may live. */
  private static readonly LOADING_MIN_MS = 400;

  private _mode: PlaybackMode = 'live';
  private _steps: PlaybackStep[] = [];
  /** Cached once per load — never rebuilt per emit (an archive can be 300 steps). */
  private _stepLabels: string[] = [];
  /**
   * Fractional index into `_steps` — the transport's clock.
   *
   * A frame cursor rather than a virtual timestamp: with equal dwell, position
   * IS a frame number, and there is no instant between two captures for a
   * flip-book to be "at".
   */
  private _frameCursor = 0;
  /** Real ms one frame is held at 1x — derived from the archive's own size. */
  private _frameMs = VoyageRadarPlaybackController.MAX_FRAME_MS;
  private _currentIndex = 0;
  private _lastRenderedIndex = -1;
  private _isPlaying = false;
  private _speed = 1;
  private _showLabels = false;
  /** Count bubbles instead of every hull. On until the operator says otherwise. */
  private _clusterVessels = true;
  private _playTimer: any = null;

  // ── Playback load ────────────────────────────────────────────────────────────
  private _playbackLoading = false;
  /** The delayed, drawable form of `_playbackLoading` — see LOADING_DELAY_MS. */
  private _playbackLoadingVisible = false;
  private _pendingPlayback: PendingPlaybackRef | null = null;
  private _loadingDelayTimer: any = null;
  private _loadingHoldTimer: any = null;
  /** When the pending row went on screen, for the minimum-hold arithmetic. */
  private _loadingShownAt = 0;
  /** Snapshots the window held, whether or not they arrived. */
  private _expectedFrames = 0;
  /** Of those, how many did not — 0 when the whole window loaded. */
  private _skippedFrames = 0;
  /**
   * Generation counter for in-flight loads.
   *
   * A replay is N signed URLs and N storage reads, and the user is free to close
   * playback, untick the sensor or switch task while they are in the air. Without
   * this the resolution would walk straight past the teardown and install its
   * frames over a map that had just been cleared. Bumped by every teardown, and
   * compared after the await.
   */
  private _loadToken = 0;
  /**
   * How the last entry ended — what `enterHistoryPlayback` tells the user about.
   *
   * A verdict rather than an inference off the frame count, because three of the
   * four ways to produce zero frames want different treatment and two of them
   * want none at all.
   */
  private _lastEntry: 'ok' | 'empty' | 'failed' | 'superseded' = 'ok';

  /** Current toolbar mode — template uses this to pick the live vs playback slot. */
  get mode(): PlaybackMode {
    return this._mode;
  }

  /** True while a replay's frames are being fetched — the gate, not the spinner. */
  get playbackLoading(): boolean {
    return this._playbackLoading;
  }

  /** Host loads the saved refresh-history snapshots that fall in [from, to]. */
  onLoadPlaybackSteps:
    | ((fromIso: string, toIso: string) => Promise<PlaybackStep[]>)
    | null = null;
  /**
   * Host describes the load ABOUT to run, synchronously, so the wait can name it.
   *
   * Split from the load itself because the two answer at different times: this
   * one has to answer on the click (the pending row is drawn against it), while
   * the load resolves two round trips per frame later. The host reads both off
   * the same `refreshHistory` window, so the count shown can never disagree with
   * the count fetched. Null-safe: without it the wait is anonymous, not broken.
   */
  onDescribePlayback: ((fromIso: string, toIso: string) => PendingPlaybackRef | null) | null =
    null;
  /**
   * Host answers whether the archive holds enough replayable snapshots to be a
   * timeline. Read on every change-detection pass by the toolbar's entry
   * control, so it must stay cheap and allocation-free — see
   * `VoyageMapController.canPlayHistory`.
   *
   * Null-safe in the permissive direction: an unwired host leaves the control
   * visible and `enterHistoryPlayback`'s own empty/failed dialogs explain the
   * outcome, which is what happened before this gate existed.
   */
  onCanPlayHistory: (() => boolean) | null = null;
  /** Host renders one frame (or clears the frame when step is null). */
  onRenderStep: ((step: PlaybackStep | null, showLabels: boolean) => void) | null = null;
  /** Host reacts to the user closing playback via the toolbar X (e.g. reset the toggle). */
  onExit: (() => void) | null = null;
  /** Host is told when "Playback history" found no frames to replay. */
  onHistoryEmpty: (() => void) | null = null;
  /**
   * Host is told when frames EXISTED and none of them loaded.
   *
   * A separate hook from the one above because the two are different accidents
   * with different advice. "Refresh the area at least twice to build a timeline"
   * is right for an archive that was never built and actively misleading for a
   * signed-URL round trip that failed — which is what the user used to be told,
   * since a dropped fetch and an empty window both arrived here as zero steps.
   */
  onHistoryFailed: (() => void) | null = null;

  // ── Host callbacks (set by the map component) ────────────────────────────────
  /** Pull the latest vessel positions for the active collection. */
  onRefresh: (() => void | Promise<void>) | null = null;
  /** Open the collection-aware history date picker. */
  override requestHistory(): void {
    this.onRequestHistory?.();
  }
  onRequestHistory: (() => void) | null = null;
  /** Apply a vessel-type filter to the current render. */
  onTypeFilterChange: ((selected: Set<string>) => void) | null = null;
  /**
   * Show/hide the vessel name labels on whatever is drawn.
   *
   * A callback of its own rather than a re-render, because the live view has no
   * frame to re-render: in live mode `_steps` is empty, so the render path can
   * only hand the host a null step — which means "clear the frame", not "repaint
   * the live layer". Mirrors `VoyageTrackController.onLabelsToggle`.
   */
  onLabelsToggle: ((show: boolean) => void) | null = null;
  /**
   * Group nearby vessels into count bubbles, or draw every hull.
   *
   * Like the labels callback and for the same reason: in live mode there is no
   * frame to re-render, and the host repaints what is drawn off this instead.
   */
  onClustersToggle: ((cluster: boolean) => void) | null = null;

  constructor() {
    super();
    this._emit();
  }

  snapshot(): VoyageRadarPlaybackState {
    return this._state$.value;
  }

  private _emit(): void {
    this._state$.next({
      ...EMPTY_VR_PLAYBACK_STATE,
      mode: this._mode,
      isPlaying: this._isPlaying,
      currentIndex: this._currentIndex,
      stepCount: this._steps.length,
      stepLabels: this._stepLabels,
      speed: this._speed,
      showLabels: this._showLabels,
      clusterVessels: this._clusterVessels,
      typeFilter: new Set(this._typeFilter),
      availableTypes: this._availableTypes,
      lastUpdated: this._lastUpdated,
      loading: this._loading,
      playbackLoading: this._playbackLoading,
      playbackLoadingVisible: this._playbackLoadingVisible,
      pendingPlayback: this._pendingPlayback,
      playbackRefreshId: this._steps[this._currentIndex]?.refreshId ?? null,
      playbackSkipped: this._skippedFrames,
    });
  }

  /**
   * Called by the host once vessel data is on the map: stamps the "Updated …"
   * timestamp and (optionally) refreshes the ship-type categories.
   * `atIso` lets the host stamp a recalled refresh-history snapshot with the
   * time it was captured; omit it for a live pull (defaults to now).
   */
  markUpdated(
    availableTypes: PlaybackAvailableType[] = this._availableTypes,
    atIso?: string | null
  ): void {
    this._availableTypes = availableTypes;
    const at = atIso ? new Date(atIso) : null;
    this._lastUpdated =
      at && !isNaN(at.getTime()) ? at.toISOString() : new Date().toISOString();
    this._loading = false;
    this._emit();
  }

  override refresh(): void {
    if (this._loading) return;
    this._loading = true;
    this._emit();
    Promise.resolve(this.onRefresh?.()).finally(() => {
      // markUpdated() is expected from the host once render completes; guard here
      // in case the host does not report back.
      if (this._loading) {
        this._loading = false;
        this._emit();
      }
    });
  }

  setTypeFilter(selected: Set<string>): void {
    this._typeFilter = new Set(selected);
    // Emit first so snapshot() reflects the new filter before any callback reads it.
    this._emit();
    this.onTypeFilterChange?.(this._typeFilter);
    if (this._mode === 'playback') this._renderIndex(this._currentIndex, true);
  }

  /** Host pushes the ship-type categories it computed from the loaded data. */
  override setAvailableTypes(types: PlaybackAvailableType[]): void {
    this._availableTypes = types ?? [];
    this._emit();
  }

  /**
   * Drops the categories AND the selection together — for when Voyage Radar
   * vessels leave the map for good (cleared, unchecked, task switched).
   *
   * Deliberately distinct from `setAvailableTypes([])`, which the host calls on
   * every layer teardown INCLUDING the one in the middle of swapping snapshots.
   * The categories belong to whatever is drawn and must go with it; the
   * selection belongs to the user and has to survive a swap, or stepping
   * through history with a filter on would drop it at every step.
   *
   * No `onTypeFilterChange` callback: there is no layer left to re-filter, and
   * firing it would re-enter the host's render path over a cleared map.
   */
  resetTypeFilter(): void {
    if (!this._availableTypes.length && !this._typeFilter.size) return;
    this._availableTypes = [];
    this._typeFilter = new Set<string>();
    this._emit();
  }

  /**
   * Two frames minimum, and a POINT task never qualifies — the same rule the
   * sidebar's Playback pill applies, answered here off the host's own count of
   * what a replay would actually load.
   */
  override get canPlayHistory(): boolean {
    return this.onCanPlayHistory ? this.onCanPlayHistory() : true;
  }

  /** Single "playback history" entry: load ALL saved refreshes and play them. */
  override async enterHistoryPlayback(): Promise<number> {
    const fromIso = new Date(0).toISOString();
    const toIso = new Date().toISOString();
    const count = await this.enterPlayback(fromIso, toIso);
    // 'superseded' says nothing: the user cancelled this replay themselves, or a
    // newer one owns the map now, and neither wants a dialog about it.
    if (this._lastEntry === 'failed') this.onHistoryFailed?.();
    else if (this._lastEntry === 'empty') this.onHistoryEmpty?.();
    return count;
  }

  // ── Transport: drives the refresh-history snapshot flip-book ─────────────────
  togglePlay(): void {
    this._isPlaying ? this.pause() : this.play();
  }

  play(): void {
    if (this._isPlaying || this._steps.length < 2) return;
    // Restart from the beginning when parked at the end.
    if (this._frameCursor >= this._lastIndex()) this._frameCursor = 0;
    this._isPlaying = true;
    this._playTimer = setInterval(
      () => this._tick(),
      VoyageRadarPlaybackController.TICK_MS
    );
    this._emit();
  }

  pause(): void {
    this._isPlaying = false;
    if (this._playTimer) {
      clearInterval(this._playTimer);
      this._playTimer = null;
    }
    this._emit();
  }

  seek(index: number): void {
    if (!this._steps.length) return;
    const i = Math.max(0, Math.min(index, this._lastIndex()));
    this._currentIndex = i;
    this._frameCursor = i;
    this._renderIndex(i);
    this._emit();
  }

  stepForward(): void { this.seek(this._currentIndex + 1); }
  stepBack(): void { this.seek(this._currentIndex - 1); }
  skipToStart(): void { this.seek(0); }
  skipToEnd(): void { this.seek(this._lastIndex()); }

  setSpeed(speed: number): void {
    this._speed = speed;
    this._emit();
  }

  /**
   * The T control — vessel name labels, in the live bar as well as in playback.
   *
   * Emit BEFORE calling out, same as `setTypeFilter`: the host reads the flag
   * back off `snapshot()` when it re-labels a layer it rebuilt (a refresh
   * replaces the live layer wholesale), so the new value has to be published
   * before anything can read it.
   *
   * The frame re-render is gated on playback because it is only a repaint of
   * the CURRENT frame; in live mode there is no frame, and the host repaints the
   * live layer off the callback above.
   */
  toggleLabels(): void {
    this._showLabels = !this._showLabels;
    this._emit();
    this.onLabelsToggle?.(this._showLabels);
    if (this._mode === 'playback') this._renderIndex(this._currentIndex, true);
  }

  /**
   * Group nearby vessels into count bubbles, or draw every hull.
   *
   * Emits before calling out, like the two above — the host reads the flag off
   * `snapshot()` when it repaints a layer it rebuilt.
   *
   * No frame re-render even in playback, unlike labels: clustering only decides
   * which of the markers already on screen are shown, so the host repaints them
   * in place. Rebuilding the frame would throw away and recreate a thousand
   * markers to answer a question about the ones that are already there.
   */
  override toggleClusters(): void {
    this._clusterVessels = !this._clusterVessels;
    this._emit();
    this.onClustersToggle?.(this._clusterVessels);
  }

  /**
   * Load the saved refresh-history snapshots for the window and enter playback.
   * Resolves with the frame count (0 = nothing played — see
   * `enterHistoryPlayback` for how an empty archive is told from a failed load).
   *
   * Starts the clock on arrival rather than landing paused on frame 0. The
   * control that gets here is labelled Playback and the panel and ON MAP bar
   * both report "Playing" off the mode alone, so a paused entry was the feature
   * announcing itself as running and then not running. AISstream's history entry
   * has always played on arrival for the same reason.
   */
  async enterPlayback(fromIso: string, toIso: string): Promise<number> {
    // A second entry over a load already in the air would fetch the same window
    // twice and race its own frames onto the map.
    if (this._playbackLoading) {
      this._lastEntry = 'superseded';
      return 0;
    }
    const wasPlayback = this._mode === 'playback';
    this.pause();

    const pending = this.onDescribePlayback?.(fromIso, toIso) ?? null;
    this._expectedFrames = pending?.frames ?? 0;
    this._skippedFrames = 0;
    this._lastEntry = 'ok';
    const token = ++this._loadToken;
    this._setPlaybackLoading(true, pending);

    let steps: PlaybackStep[] = [];
    try {
      steps = (await this.onLoadPlaybackSteps?.(fromIso, toIso)) ?? [];
    } catch {
      steps = [];
    }

    // Closed, exited or torn down while the frames were in the air. Everything
    // this load owned has already been dropped by whoever bumped the token, so
    // there is nothing to undo and — crucially — nothing to report: a dialog
    // explaining a replay the user has just cancelled is worse than silence.
    if (token !== this._loadToken) {
      this._lastEntry = 'superseded';
      return 0;
    }

    steps = steps
      .filter((s) => s && s.time && Array.isArray(s.features))
      .sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());

    if (!steps.length) {
      // Records the window did hold, none of which arrived, is a failed load —
      // not an archive that was never built.
      this._lastEntry = this._expectedFrames > 0 ? 'failed' : 'empty';
      this._setPlaybackLoading(false);
      // Re-entering over a running playback and finding nothing used to flip the
      // mode back to live and stop there, leaving the last frame drawn over a
      // hidden live layer with no toolbar left that could take it off.
      if (wasPlayback) this.resumeLive();
      else {
        this._mode = 'live';
        this._emit();
      }
      return 0;
    }

    this._steps = steps;
    this._stepLabels = steps.map((s) => s.time);
    this._skippedFrames = Math.max(0, this._expectedFrames - steps.length);
    this._frameMs = VoyageRadarPlaybackController._dwellFor(steps.length);
    // Every load starts at 1x, like `VoyageTrackController.loadTrack`: the
    // multiplier describes this archive's pace, so carrying 8x over from a
    // 200-frame record into a 3-frame one would flick past the whole thing.
    this._speed = 1;
    this._mode = 'playback';
    this._currentIndex = 0;
    this._frameCursor = 0;
    this._lastRenderedIndex = -1;
    // Not held to the minimum: the first frame is about to be drawn, and that is
    // a better completion signal than any spinner. See `_hideLoadingNow`.
    this._hideLoadingNow(false);
    this._renderIndex(0);
    this._emit();
    this.play();
    return steps.length;
  }

  resumeLive(): void {
    this.pause();
    this._resetPlayback();
    this.onRenderStep?.(null, this._showLabels);
    this._emit();
    this.onExit?.();
  }

  close(): void {
    this.pause();
    this._resetPlayback();
    this._lastUpdated = null;
    this._loading = false;
    this._typeFilter = new Set<string>();
    // The categories go with the selection: this is a full teardown, and
    // leaving them behind would keep the Vessel Types section on screen over a
    // map with nothing on it.
    this._availableTypes = [];
    this.onRenderStep?.(null, this._showLabels);
    this._emit();
  }

  // ── Internals ────────────────────────────────────────────────────────────────
  /** Everything a replay owns, dropped together. Never emits — callers do. */
  private _resetPlayback(): void {
    this._mode = 'live';
    this._steps = [];
    this._stepLabels = [];
    this._currentIndex = 0;
    this._frameCursor = 0;
    this._lastRenderedIndex = -1;
    this._expectedFrames = 0;
    this._skippedFrames = 0;
    this._playbackLoading = false;
    // Orphans any load still in the air, so its frames cannot land on the map
    // this call has just cleared. See `_loadToken`.
    this._loadToken++;
    // Through the reveal rather than straight at the flag: dropping the truth
    // without closing its timers would strand a pending row on screen with no
    // load behind it and nothing left to take it down.
    this._hideLoadingNow(false);
  }

  /**
   * How long one frame is held at 1x, for an archive of `count` snapshots.
   *
   * The whole record inside the budget where the count allows it, clamped at
   * both ends so a 2-frame archive is not a stare and a 300-frame one is not a
   * strobe. See MIN_FRAME_MS / MAX_FRAME_MS.
   */
  private static _dwellFor(count: number): number {
    const share = VoyageRadarPlaybackController.BASE_PLAY_MS / Math.max(1, count);
    return Math.min(
      VoyageRadarPlaybackController.MAX_FRAME_MS,
      Math.max(VoyageRadarPlaybackController.MIN_FRAME_MS, share)
    );
  }

  private _lastIndex(): number {
    return Math.max(0, this._steps.length - 1);
  }

  private _tick(): void {
    const prevIndex = this._currentIndex;
    // Advance the cursor by (real tick / dwell) frames, scaled by the multiplier.
    this._frameCursor +=
      (VoyageRadarPlaybackController.TICK_MS / this._frameMs) * this._speed;
    const last = this._lastIndex();
    if (this._frameCursor >= last) {
      this._frameCursor = last;
      this._currentIndex = last;
      this._renderIndex(last);
      this.pause();
      return;
    }
    this._currentIndex = Math.floor(this._frameCursor);
    this._renderIndex(this._currentIndex);
    // Only emit when the visible frame actually advanced — avoids ~10 redundant
    // state emits/sec (and the toolbar change-detection they trigger).
    if (this._currentIndex !== prevIndex) this._emit();
  }

  private _renderIndex(i: number, force = false): void {
    if (!force && i === this._lastRenderedIndex) return;
    this._lastRenderedIndex = i;
    this.onRenderStep?.(this._steps[i] ?? null, this._showLabels);
  }

  // ── Load reveal ──────────────────────────────────────────────────────────────
  /**
   * Open or close a playback load.
   *
   * The snapshot twin of `VoyageTrackController.setTrackLoading`, and the same
   * two flags for the same reason. `playbackLoading` is the truth and is what
   * gates a second entry from the first millisecond; `playbackLoadingVisible` is
   * what may be DRAWN and lags by `LOADING_DELAY_MS`, so a window served back
   * out of the step cache never blinks a spinner. Every gate reads the first;
   * every template the second.
   *
   * Private, unlike the track's: a track load is owned by the map controller
   * (two round trips it runs itself), while a replay is loaded through this
   * controller's own `enterPlayback` — so nothing outside has a load to report.
   */
  private _setPlaybackLoading(
    loading: boolean,
    pending: PendingPlaybackRef | null = null
  ): void {
    this._playbackLoading = loading;
    if (loading) {
      this._pendingPlayback = pending;
      this._armLoadingReveal();
      this._emit();
      return;
    }
    this._closeLoadingReveal();
    this._emit();
  }

  /** Starts the delay after which a load becomes worth drawing. */
  private _armLoadingReveal(): void {
    this._clearLoadingTimers();
    if (this._playbackLoadingVisible) return; // already on screen from a prior load
    this._loadingDelayTimer = setTimeout(() => {
      this._loadingDelayTimer = null;
      // Re-checked rather than assumed: the frames can land inside the delay and
      // clear the load without this timer having been the one to fire.
      if (!this._playbackLoading) return;
      this._playbackLoadingVisible = true;
      this._loadingShownAt = Date.now();
      this._emit();
    }, VoyageRadarPlaybackController.LOADING_DELAY_MS);
  }

  /**
   * Ends a load that put nothing on the map. Holds the pending row to
   * `LOADING_MIN_MS` when it is already showing, so the spinner does not blink
   * out one frame before the dialog that explains why.
   */
  private _closeLoadingReveal(): void {
    this._clearLoadingTimers();
    if (!this._playbackLoadingVisible) {
      this._pendingPlayback = null;
      return;
    }
    const shownFor = Date.now() - this._loadingShownAt;
    const left = VoyageRadarPlaybackController.LOADING_MIN_MS - shownFor;
    if (left <= 0) {
      this._hideLoadingNow(false);
      return;
    }
    this._loadingHoldTimer = setTimeout(() => {
      this._loadingHoldTimer = null;
      // A new load may have opened during the hold — it owns the row now.
      if (this._playbackLoading) return;
      this._hideLoadingNow(true);
    }, left);
  }

  /**
   * Takes the pending row down at once, whatever the hold had left.
   *
   * Called by `enterPlayback` on success: the first frame appearing is a
   * completion signal no spinner can improve on, and leaving the row up over it
   * would have the panel claiming a load is in flight while the map plays its
   * result.
   */
  private _hideLoadingNow(emit: boolean): void {
    this._clearLoadingTimers();
    this._playbackLoadingVisible = false;
    this._pendingPlayback = null;
    this._loadingShownAt = 0;
    if (emit) this._emit();
  }

  private _clearLoadingTimers(): void {
    if (this._loadingDelayTimer) {
      clearTimeout(this._loadingDelayTimer);
      this._loadingDelayTimer = null;
    }
    if (this._loadingHoldTimer) {
      clearTimeout(this._loadingHoldTimer);
      this._loadingHoldTimer = null;
    }
  }
}
